import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, MapPin, Users, Shield, Trash2, Bell, Mail, Clock, AlertTriangle, CheckCircle, CalendarDays, TrendingUp, Smartphone, ShoppingCart, Sparkles, Key, Eye, EyeOff, RefreshCw, ExternalLink, Loader2, Settings, Server, Database, CreditCard, Send, XCircle, Calendar } from 'lucide-react';
import { 
  isPushSupported, 
  getNotificationPermission, 
  requestNotificationPermission, 
  subscribeToPush, 
  unsubscribeFromPush,
  showLocalNotification 
} from '../utils/pushNotifications';

// Subscription Settings Component for Companies
const SubscriptionSettingsComponent = ({ api, subscription, refreshSubscription }) => {
  const [loading, setLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Actief', className: 'bg-emerald-100 text-emerald-700' },
      trial: { label: 'Proefperiode', className: 'bg-blue-100 text-blue-700' },
      cancelled: { label: 'Opgezegd', className: 'bg-amber-100 text-amber-700' },
      expired: { label: 'Verlopen', className: 'bg-rose-100 text-rose-700' },
      past_due: { label: 'Betaling mislukt', className: 'bg-rose-100 text-rose-700' },
      pending: { label: 'In behandeling', className: 'bg-stone-100 text-stone-700' },
      admin: { label: 'Admin', className: 'bg-purple-100 text-purple-700' },
      none: { label: 'Geen abonnement', className: 'bg-stone-100 text-stone-700' }
    };
    const config = statusConfig[status] || statusConfig.none;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await api.post('/subscription/cancel');
      toast.success('Abonnement wordt beëindigd aan het einde van de huidige periode');
      setCancelDialogOpen(false);
      if (refreshSubscription) refreshSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij opzeggen abonnement');
    } finally {
      setCancelling(false);
    }
  };

  const canCancel = subscription?.status === 'active' || subscription?.status === 'trial';
  const isCancelled = subscription?.status === 'cancelled';

  return (
    <div className="space-y-6">
      <Card className="card-base">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Abonnement Status
          </CardTitle>
          <CardDescription>
            Bekijk en beheer je abonnement
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status Overview */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-4 rounded-xl bg-stone-50 border border-stone-200">
              <p className="text-sm text-stone-500 mb-1">Status</p>
              <div className="flex items-center gap-2">
                {getStatusBadge(subscription?.status)}
              </div>
            </div>
            <div className="p-4 rounded-xl bg-stone-50 border border-stone-200">
              <p className="text-sm text-stone-500 mb-1">
                {subscription?.status === 'trial' ? 'Proefperiode eindigt' : 'Volgende factuurdatum'}
              </p>
              <p className="font-semibold text-[#1C1917]">
                {formatDate(subscription?.trial_end || subscription?.subscription_end)}
              </p>
            </div>
          </div>

          {/* Subscription Details */}
          {subscription?.status !== 'none' && (
            <div className="p-4 rounded-xl border border-stone-200 bg-white">
              <h4 className="font-semibold text-[#1C1917] mb-3">Abonnement Details</h4>
              <div className="space-y-2 text-sm">
                {subscription?.subscription_start && (
                  <div className="flex justify-between">
                    <span className="text-stone-500">Startdatum:</span>
                    <span className="text-[#1C1917]">{formatDate(subscription.subscription_start)}</span>
                  </div>
                )}
                {subscription?.trial_end && subscription?.status === 'trial' && (
                  <div className="flex justify-between">
                    <span className="text-stone-500">Proefperiode tot:</span>
                    <span className="text-[#1C1917]">{formatDate(subscription.trial_end)}</span>
                  </div>
                )}
                {subscription?.subscription_end && subscription?.status !== 'trial' && (
                  <div className="flex justify-between">
                    <span className="text-stone-500">Huidige periode tot:</span>
                    <span className="text-[#1C1917]">{formatDate(subscription.subscription_end)}</span>
                  </div>
                )}
                {isCancelled && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-amber-700 text-sm">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      Je abonnement is opgezegd en eindigt op {formatDate(subscription.subscription_end)}.
                      Je hebt nog toegang tot deze datum.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Cancel Button */}
          {canCancel && (
            <div className="pt-4 border-t border-stone-200">
              <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50">
                    <XCircle className="w-4 h-4 mr-2" />
                    Abonnement Opzeggen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Abonnement Opzeggen</DialogTitle>
                    <DialogDescription>
                      Weet je zeker dat je je abonnement wilt opzeggen?
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-amber-700 text-sm">
                        <AlertTriangle className="w-4 h-4 inline mr-1" />
                        Je houdt toegang tot het dashboard tot het einde van je huidige betaalperiode 
                        ({formatDate(subscription?.subscription_end || subscription?.trial_end)}).
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
                      Annuleren
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                    >
                      {cancelling ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Bezig...
                        </>
                      ) : (
                        'Ja, opzeggen'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* No subscription */}
          {subscription?.status === 'none' && (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500 mb-4">Je hebt nog geen actief abonnement.</p>
              <Button 
                onClick={() => window.location.href = '/subscription'}
                className="btn-primary"
              >
                Abonnement Kiezen
              </Button>
            </div>
          )}

          {/* Expired subscription */}
          {subscription?.status === 'expired' && (
            <div className="text-center py-8">
              <XCircle className="w-12 h-12 text-rose-300 mx-auto mb-4" />
              <p className="text-stone-500 mb-4">Je abonnement is verlopen.</p>
              <Button 
                onClick={() => window.location.href = '/subscription'}
                className="btn-primary"
              >
                Abonnement Vernieuwen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// AI Settings Component
const AISettingsComponent = ({ api }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/ai/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching AI settings:', error);
      toast.error('Fout bij ophalen AI instellingen');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKey = async () => {
    if (!apiKey.trim()) {
      toast.error('Voer een API key in');
      return;
    }
    
    if (!apiKey.startsWith('sk-')) {
      toast.error('Ongeldige API key. De key moet beginnen met "sk-"');
      return;
    }

    setSaving(true);
    try {
      await api.put('/ai/settings', { openai_api_key: apiKey });
      toast.success('API key opgeslagen');
      setApiKey('');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKey = async () => {
    if (!window.confirm('Weet je zeker dat je de API key wilt verwijderen?')) return;
    
    try {
      await api.delete('/ai/settings/key');
      toast.success('API key verwijderd');
      fetchSettings();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleTestKey = async () => {
    setTesting(true);
    try {
      await api.post('/ai/test-key');
      toast.success('API key is geldig!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'API key test mislukt');
    } finally {
      setTesting(false);
    }
  };

  const handleModelChange = async (model) => {
    try {
      await api.put('/ai/settings', { openai_model: model });
      setSettings({ ...settings, openai_model: model });
      toast.success('Model gewijzigd');
    } catch (error) {
      toast.error('Fout bij wijzigen model');
    }
  };

  const handleToggleAI = async (enabled) => {
    try {
      await api.put('/ai/settings', { ai_enabled: enabled });
      setSettings({ ...settings, ai_enabled: enabled });
      toast.success(enabled ? 'AI ingeschakeld' : 'AI uitgeschakeld');
    } catch (error) {
      toast.error('Fout bij wijzigen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* API Key Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-orange-600" />
            OpenAI API Key
          </CardTitle>
          <CardDescription>
            Configureer je eigen OpenAI API key om AI functies te gebruiken
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings?.openai_api_key_set ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-700 font-medium">API key is geconfigureerd</span>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={handleTestKey}
                  disabled={testing}
                  data-testid="test-api-key-btn"
                >
                  {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Test Key
                </Button>
                <Button 
                  variant="outline" 
                  className="text-red-600 hover:bg-red-50"
                  onClick={handleDeleteKey}
                  data-testid="delete-api-key-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Verwijderen
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <span className="text-yellow-700">Geen API key geconfigureerd - AI functies zijn uitgeschakeld</span>
              </div>
              
              <div className="space-y-2">
                <Label>OpenAI API Key</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showKey ? "text" : "password"}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="sk-..."
                      data-testid="api-key-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowKey(!showKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button 
                    onClick={handleSaveKey}
                    disabled={saving || !apiKey}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="save-api-key-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Opslaan'}
                  </Button>
                </div>
              </div>
              
              <Button
                variant="link"
                className="p-0 h-auto text-orange-600"
                onClick={() => window.open('https://platform.openai.com/api-keys', '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                OpenAI API key aanmaken
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Model Selection Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-orange-600" />
            AI Model
          </CardTitle>
          <CardDescription>
            Kies welk OpenAI model je wilt gebruiken voor AI analyses
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Model</Label>
            <Select 
              value={settings?.openai_model || 'gpt-4o-mini'} 
              onValueChange={handleModelChange}
              disabled={!settings?.openai_api_key_set}
            >
              <SelectTrigger data-testid="model-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {settings?.available_models?.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    <div className="flex flex-col">
                      <span className="font-medium">{model.name}</span>
                      <span className="text-xs text-gray-500">{model.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            {settings?.available_models?.map((model) => (
              <div 
                key={model.id}
                className={`p-3 rounded-lg border ${settings?.openai_model === model.id ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}
              >
                <div className="font-medium">{model.name}</div>
                <div className="text-sm text-gray-500">{model.description}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* AI Toggle Card */}
      <Card>
        <CardHeader>
          <CardTitle>AI Functies</CardTitle>
          <CardDescription>
            Schakel AI functies in of uit voor je account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">AI Analyse ingeschakeld</div>
              <div className="text-sm text-gray-500">
                Wanneer uitgeschakeld worden geen AI calls gemaakt
              </div>
            </div>
            <Switch
              checked={settings?.ai_enabled ?? true}
              onCheckedChange={handleToggleAI}
              disabled={!settings?.openai_api_key_set}
              data-testid="ai-toggle"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// User Management Component
const UserManagement = ({ users, setUsers, api, modules }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    permissions: {}
  });

  const medewerkers = users.filter(u => u.role === 'medewerker');
  const companyOwners = users.filter(u => u.role === 'company');

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/create-employee', form);
      toast.success('Medewerker aangemaakt');
      setShowDialog(false);
      setForm({ name: '', email: '', password: '', permissions: {} });
      // Refresh users
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij aanmaken');
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Weet je zeker dat je ${userName} wilt verwijderen?`)) return;
    try {
      await api.delete(`/auth/user/${userId}`);
      toast.success('Medewerker verwijderd');
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij verwijderen');
    }
  };

  const handleUpdatePermission = async (userId, module, value) => {
    try {
      const targetUser = users.find(u => u.id === userId);
      const newPermissions = { ...targetUser.permissions, [module]: value };
      await api.put(`/auth/permissions/${userId}`, { user_id: userId, permissions: newPermissions });
      // Update local state
      setUsers(users.map(u => u.id === userId ? { ...u, permissions: newPermissions } : u));
      toast.success('Rechten bijgewerkt');
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  return (
    <div className="space-y-6">
      {/* Create User Card */}
      <Card className="card-base">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Medewerkers</CardTitle>
            <p className="text-sm text-[#57534E] mt-1">
              {medewerkers.length} medewerker{medewerkers.length !== 1 ? 's' : ''} actief
            </p>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Nieuwe Medewerker
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Nieuwe Medewerker Toevoegen</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Naam *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Volledige naam"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="medewerker@bedrijf.nl"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Wachtwoord *</Label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Minimaal 6 karakters"
                    minLength={6}
                    required
                  />
                </div>
                <div className="pt-2">
                  <Label className="text-sm font-medium">Module Toegang</Label>
                  <p className="text-xs text-[#57534E] mb-3">Selecteer welke modules deze medewerker mag zien</p>
                  <div className="grid grid-cols-2 gap-2">
                    {modules.map(m => (
                      <label key={m.key} className="flex items-center gap-2 p-2 rounded-lg border hover:bg-stone-50 cursor-pointer">
                        <Switch
                          checked={form.permissions[m.key] !== false}
                          onCheckedChange={(checked) => setForm({
                            ...form,
                            permissions: { ...form.permissions, [m.key]: checked }
                          })}
                        />
                        <span className="text-sm">{m.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Button type="submit" className="w-full btn-primary">
                  Medewerker Aanmaken
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {medewerkers.length === 0 ? (
            <div className="text-center py-8 text-[#57534E]">
              <Users className="w-12 h-12 mx-auto mb-4 text-stone-300" />
              <p className="font-medium">Nog geen medewerkers</p>
              <p className="text-sm">Voeg medewerkers toe om toegang te geven tot het dashboard</p>
            </div>
          ) : (
            <div className="space-y-3">
              {medewerkers.map(employee => (
                <div key={employee.id} className="flex items-center justify-between p-4 rounded-lg border bg-white">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#1A2E05] flex items-center justify-center text-white font-medium">
                      {employee.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">{employee.name}</p>
                      <p className="text-sm text-[#57534E]">{employee.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      onClick={() => handleDelete(employee.id, employee.name)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permissions Card */}
      {medewerkers.length > 0 && (
        <Card className="card-base">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#C2410C]" />
              Module Rechten
            </CardTitle>
            <p className="text-sm text-[#57534E]">Bepaal welke modules elke medewerker kan zien</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead className="min-w-[150px]">Medewerker</TableHead>
                    {modules.map(m => (
                      <TableHead key={m.key} className="text-center text-xs px-2 min-w-[80px]">
                        {m.label.split(' ')[0]}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {medewerkers.map(employee => (
                    <TableRow key={employee.id} className="table-row">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-[#1A2E05] flex items-center justify-center text-white text-sm">
                            {employee.name?.charAt(0).toUpperCase()}
                          </div>
                          <span className="truncate max-w-[100px]">{employee.name}</span>
                        </div>
                      </TableCell>
                      {modules.map(m => (
                        <TableCell key={m.key} className="text-center">
                          <Switch
                            checked={employee.permissions?.[m.key] !== false}
                            onCheckedChange={(checked) => handleUpdatePermission(employee.id, m.key, checked)}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ondernemers Info */}
      <Card className="card-base bg-stone-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-[#1A2E05] mt-0.5" />
            <div>
              <p className="font-medium text-sm">Bedrijfseigenaren ({companyOwners.length})</p>
              <p className="text-xs text-[#57534E]">
                Bedrijfseigenaren hebben volledige toegang tot alle modules en instellingen.
                {companyOwners.map(o => o.name).join(', ')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const InstellingenPage = () => {
  const { api, user, subscription, refreshSubscription } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [locationDialog, setLocationDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'users');
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [testingAlerts, setTestingAlerts] = useState(false);

  const [locationForm, setLocationForm] = useState({
    name: '',
    address: '',
    capacity: ''
  });

  const [alertConfig, setAlertConfig] = useState({
    is_active: false,
    email: '',
    food_cost_threshold: 30,
    personnel_cost_threshold: 35,
    revenue_below_target_threshold: 10,
    occupancy_threshold: 50,
    noshow_threshold: 10,
    inkoop_cost_threshold: 25,
    inkoop_trend_threshold: 20,
    email_alerts: true,
    in_app_alerts: true,
    daily_summary: true,
    weekly_summary: true,
    summary_time: '08:00',
    summary_day: 'maandag',
    // Revenue reminder settings
    revenue_reminder_enabled: false,
    revenue_reminder_frequency: 'dagelijks',
    revenue_reminder_time: '18:00',
    revenue_reminder_day: 'maandag',
    // Push notification settings
    push_enabled: false,
    push_subscription: null,
    // Smart reminder settings
    smart_reminder: true  // Only send if revenue not filled in
  });

  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [enablingPush, setEnablingPush] = useState(false);

  useEffect(() => {
    fetchData();
    // Check push notification support
    setPushSupported(isPushSupported());
    setPushPermission(getNotificationPermission());
  }, []);

  const fetchData = async () => {
    try {
      const [locRes, usersRes, alertRes] = await Promise.all([
        api.get('/locations'),
        api.get('/users'),
        api.get('/alerts/config')
      ]);
      setLocations(locRes.data);
      setUsers(usersRes.data);
      if (alertRes.data) {
        setAlertConfig({
          ...alertConfig,
          ...alertRes.data,
          email: alertRes.data.email || user?.email || ''
        });
      }
    } catch (error) {
      toast.error('Fout bij laden data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    try {
      await api.post('/locations', {
        ...locationForm,
        capacity: parseInt(locationForm.capacity) || 0
      });
      toast.success('Locatie toegevoegd');
      setLocationDialog(false);
      setLocationForm({ name: '', address: '', capacity: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleDeleteLocation = async (id) => {
    if (!window.confirm('Locatie verwijderen?')) return;
    try {
      await api.delete(`/locations/${id}`);
      toast.success('Locatie verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleUpdatePermission = async (userId, module, value) => {
    try {
      const targetUser = users.find(u => u.id === userId);
      const newPermissions = { ...targetUser.permissions, [module]: value };
      await api.put(`/auth/permissions/${userId}`, { user_id: userId, permissions: newPermissions });
      toast.success('Rechten bijgewerkt');
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken rechten');
    }
  };

  const handleSaveAlertConfig = async () => {
    setSavingAlerts(true);
    try {
      await api.post('/alerts/config', {
        email: alertConfig.email,
        food_cost_threshold: alertConfig.food_cost_threshold,
        personnel_cost_threshold: alertConfig.personnel_cost_threshold,
        revenue_below_target_threshold: alertConfig.revenue_below_target_threshold,
        occupancy_threshold: alertConfig.occupancy_threshold,
        noshow_threshold: alertConfig.noshow_threshold,
        inkoop_cost_threshold: alertConfig.inkoop_cost_threshold,
        inkoop_trend_threshold: alertConfig.inkoop_trend_threshold,
        email_alerts: alertConfig.email_alerts,
        in_app_alerts: alertConfig.in_app_alerts,
        daily_summary: alertConfig.daily_summary,
        weekly_summary: alertConfig.weekly_summary,
        summary_time: alertConfig.summary_time,
        summary_day: alertConfig.summary_day,
        // Revenue reminder settings
        revenue_reminder_enabled: alertConfig.revenue_reminder_enabled,
        revenue_reminder_frequency: alertConfig.revenue_reminder_frequency,
        revenue_reminder_time: alertConfig.revenue_reminder_time,
        revenue_reminder_day: alertConfig.revenue_reminder_day,
        // Smart reminder
        smart_reminder: alertConfig.smart_reminder
      });
      setAlertConfig({ ...alertConfig, is_active: true });
      toast.success('Alert instellingen opgeslagen');
    } catch (error) {
      toast.error('Fout bij opslaan alert instellingen');
    } finally {
      setSavingAlerts(false);
    }
  };

  const handleToggleAlerts = async (isActive) => {
    try {
      await api.put(`/alerts/config/toggle?is_active=${isActive}`);
      setAlertConfig({ ...alertConfig, is_active: isActive });
      toast.success(isActive ? 'Alerts geactiveerd' : 'Alerts gedeactiveerd');
    } catch (error) {
      toast.error('Fout bij wijzigen alert status');
    }
  };

  const handleTestAlerts = async () => {
    setTestingAlerts(true);
    try {
      const res = await api.post('/alerts/check');
      if (res.data.alerts_generated > 0) {
        toast.success(`${res.data.alerts_generated} alert(s) gegenereerd`);
      } else {
        toast.info('Geen drempels overschreden - alles OK!');
      }
    } catch (error) {
      toast.error('Fout bij testen alerts');
    } finally {
      setTestingAlerts(false);
    }
  };

  const handleEnablePush = async () => {
    setEnablingPush(true);
    try {
      // First request permission
      const permResult = await requestNotificationPermission();
      if (!permResult.success) {
        toast.error(permResult.error || 'Kon geen toestemming krijgen voor notificaties');
        setEnablingPush(false);
        return;
      }
      
      setPushPermission('granted');
      
      // Subscribe to push
      const subResult = await subscribeToPush();
      if (!subResult.success) {
        toast.error(subResult.error || 'Kon niet abonneren op push notificaties');
        setEnablingPush(false);
        return;
      }
      
      // Save subscription to backend
      try {
        await api.post('/alerts/push-subscription', { subscription: subResult.subscription });
      } catch (err) {
        console.log('Could not save subscription to backend');
      }
      
      setAlertConfig({ ...alertConfig, push_enabled: true, push_subscription: subResult.subscription });
      toast.success('Push notificaties ingeschakeld!');
      
      // Show test notification
      setTimeout(() => {
        showLocalNotification('Het Horeca Dashboard', 'Push notificaties zijn nu actief! 🎉');
      }, 1000);
      
    } catch (error) {
      toast.error('Fout bij inschakelen push notificaties');
    } finally {
      setEnablingPush(false);
    }
  };

  const handleDisablePush = async () => {
    try {
      await unsubscribeFromPush();
      setAlertConfig({ ...alertConfig, push_enabled: false, push_subscription: null });
      toast.success('Push notificaties uitgeschakeld');
    } catch (error) {
      toast.error('Fout bij uitschakelen push notificaties');
    }
  };

  const handleTestPushNotification = async () => {
    const success = await showLocalNotification(
      'Test Herinnering', 
      'Vergeet niet je omzet in te vullen! Dit is een test notificatie.'
    );
    if (success) {
      toast.success('Test notificatie verzonden');
    } else {
      toast.error('Kon geen test notificatie verzenden');
    }
  };

  const modules = [
    { key: 'omzet_verkoop', label: 'Omzet & Verkoop' },
    { key: 'kosten_marges', label: 'Kosten & Marges' },
    { key: 'voorraad_inkoop', label: 'Voorraad & Inkoop' },
    { key: 'personeel_planning', label: 'Personeel & Planning' },
    { key: 'gast_beleving', label: 'Gast & Beleving' },
    { key: 'reserveringen_bezetting', label: 'Reserveringen & Bezetting' },
    { key: 'marketing_acties', label: 'Marketing & Acties' },
    { key: 'trends_forecasting', label: 'Trends & Forecasting' },
    { key: 'kpi_management', label: 'KPI Management' },
    { key: 'doelen_structuur', label: 'Doelen & Structuur' },
    { key: 'acties_evaluatie', label: 'Acties & Evaluatie' }
  ];

  // Don't render until we know the user role
  if (!user) {
    return (
      <div className="p-6 md:p-8">
        <Card className="card-base">
          <CardContent className="py-12 text-center">
            <div className="animate-pulse">
              <div className="w-12 h-12 bg-stone-200 rounded-full mx-auto mb-4" />
              <div className="h-4 bg-stone-200 rounded w-32 mx-auto mb-2" />
              <div className="h-3 bg-stone-200 rounded w-48 mx-auto" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (user.role !== 'company' && user.role !== 'admin') {
    return (
      <div className="p-6 md:p-8">
        <Card className="card-base">
          <CardContent className="py-12 text-center">
            <Shield className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <p className="text-lg font-semibold">Geen toegang</p>
            <p className="text-[#57534E]">Alleen bedrijfseigenaren en admins hebben toegang tot instellingen</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading skeleton while data is being fetched
  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
              Instellingen
            </h1>
            <p className="text-[#57534E] mt-1">Laden...</p>
          </div>
        </div>
        <Card className="card-base">
          <CardContent className="py-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-stone-200 rounded w-48" />
              <div className="h-4 bg-stone-200 rounded w-32" />
              <div className="space-y-3 mt-6">
                {[1,2,3].map(i => (
                  <div key={i} className="flex items-center gap-3 p-4 border rounded-lg">
                    <div className="w-10 h-10 bg-stone-200 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-stone-200 rounded w-32" />
                      <div className="h-3 bg-stone-200 rounded w-48" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="instellingen-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Instellingen
          </h1>
          <p className="text-[#57534E] mt-1">
            Beheer gebruikers, rechten, locaties en alerts
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Gebruikers
          </TabsTrigger>
          <TabsTrigger value="permissions">Rechten</TabsTrigger>
          <TabsTrigger value="locations">Locaties</TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            AI
          </TabsTrigger>
          <TabsTrigger value="alerts" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Alerts
          </TabsTrigger>
          {user?.role !== 'admin' && (
            <TabsTrigger value="subscription" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Abonnement
            </TabsTrigger>
          )}
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          <UserManagement users={users} setUsers={setUsers} api={api} modules={modules} />
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>Locaties</CardTitle>
              <Dialog open={locationDialog} onOpenChange={setLocationDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-location-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Locatie toevoegen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nieuwe locatie</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddLocation} className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Naam</Label>
                      <Input
                        value={locationForm.name}
                        onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                        placeholder="bijv. Restaurant Centrum"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Adres</Label>
                      <Input
                        value={locationForm.address}
                        onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })}
                        placeholder="Straat 123, Stad"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Capaciteit (aantal gasten)</Label>
                      <Input
                        type="number"
                        value={locationForm.capacity}
                        onChange={(e) => setLocationForm({ ...locationForm, capacity: e.target.value })}
                        placeholder="100"
                      />
                    </div>
                    <Button type="submit" className="w-full btn-primary">
                      Toevoegen
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {locations.length === 0 ? (
                <div className="text-center py-8 text-[#57534E]">
                  <MapPin className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>Nog geen locaties</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {locations.map(location => (
                    <Card key={location.id} className="card-base">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#C2410C]/10 flex items-center justify-center">
                              <MapPin className="w-5 h-5 text-[#C2410C]" />
                            </div>
                            <div>
                              <h3 className="font-semibold">{location.name}</h3>
                              {location.address && (
                                <p className="text-sm text-[#57534E]">{location.address}</p>
                              )}
                              {location.capacity > 0 && (
                                <p className="text-sm text-[#C2410C] mt-1">
                                  <Users className="w-4 h-4 inline mr-1" />
                                  {location.capacity} gasten
                                </p>
                              )}
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-rose-600"
                            onClick={() => handleDeleteLocation(location.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Permissions Tab */}
        <TabsContent value="permissions" className="mt-6">
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Gebruikersrechten</CardTitle>
            </CardHeader>
            <CardContent>
              {users.filter(u => u.role === 'medewerker').length === 0 ? (
                <div className="text-center py-8 text-[#57534E]">
                  <Users className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>Geen medewerkers om rechten voor te beheren</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Medewerker</TableHead>
                        {modules.map(m => (
                          <TableHead key={m.key} className="text-center text-xs">
                            {m.label.split(' ')[0]}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.filter(u => u.role === 'medewerker').map(targetUser => (
                        <TableRow key={targetUser.id} className="table-row">
                          <TableCell className="font-medium">
                            <div>
                              <p>{targetUser.name}</p>
                              <p className="text-xs text-[#57534E]">{targetUser.email}</p>
                            </div>
                          </TableCell>
                          {modules.map(m => (
                            <TableCell key={m.key} className="text-center">
                              <Switch
                                checked={targetUser.permissions?.[m.key] !== false}
                                onCheckedChange={(checked) => handleUpdatePermission(targetUser.id, m.key, checked)}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Tab */}
        <TabsContent value="ai" className="mt-6">
          <AISettingsComponent api={api} />
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="mt-6 space-y-6">
          {/* Alert Status Card */}
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${alertConfig.is_active ? 'bg-green-100' : 'bg-stone-100'}`}>
                  <Bell className={`w-5 h-5 ${alertConfig.is_active ? 'text-green-600' : 'text-stone-400'}`} />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Automatische Alerts</CardTitle>
                  <p className="text-sm text-[#57534E]">
                    {alertConfig.is_active ? 'Alerts zijn actief' : 'Alerts zijn uitgeschakeld'}
                  </p>
                </div>
              </div>
              <Switch
                checked={alertConfig.is_active}
                onCheckedChange={handleToggleAlerts}
                data-testid="alerts-toggle"
              />
            </CardHeader>
          </Card>

          {/* KPI Thresholds */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                KPI Drempelwaarden
              </CardTitle>
              <p className="text-sm text-[#57534E]">Stel in wanneer u een alert wilt ontvangen</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span>Food Cost Drempel (%)</span>
                    <span className="text-sm text-[#C2410C] font-medium">{alertConfig.food_cost_threshold}%</span>
                  </Label>
                  <Input
                    type="number"
                    value={alertConfig.food_cost_threshold}
                    onChange={(e) => setAlertConfig({ ...alertConfig, food_cost_threshold: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    step="0.5"
                  />
                  <p className="text-xs text-[#57534E]">Alert wanneer food cost boven dit percentage komt</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span>Personeelskosten Drempel (%)</span>
                    <span className="text-sm text-[#C2410C] font-medium">{alertConfig.personnel_cost_threshold}%</span>
                  </Label>
                  <Input
                    type="number"
                    value={alertConfig.personnel_cost_threshold}
                    onChange={(e) => setAlertConfig({ ...alertConfig, personnel_cost_threshold: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    step="0.5"
                  />
                  <p className="text-xs text-[#57534E]">Alert wanneer personeelskosten boven dit percentage komt</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span>Omzet Onder Doel Drempel (%)</span>
                    <span className="text-sm text-[#C2410C] font-medium">{alertConfig.revenue_below_target_threshold}%</span>
                  </Label>
                  <Input
                    type="number"
                    value={alertConfig.revenue_below_target_threshold}
                    onChange={(e) => setAlertConfig({ ...alertConfig, revenue_below_target_threshold: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    step="0.5"
                  />
                  <p className="text-xs text-[#57534E]">Alert wanneer omzet meer dan dit percentage onder doel ligt</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span>Minimale Bezettingsgraad (%)</span>
                    <span className="text-sm text-[#C2410C] font-medium">{alertConfig.occupancy_threshold}%</span>
                  </Label>
                  <Input
                    type="number"
                    value={alertConfig.occupancy_threshold}
                    onChange={(e) => setAlertConfig({ ...alertConfig, occupancy_threshold: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    step="0.5"
                  />
                  <p className="text-xs text-[#57534E]">Alert wanneer bezetting onder dit percentage zakt</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span>No-Show Drempel (%)</span>
                    <span className="text-sm text-[#C2410C] font-medium">{alertConfig.noshow_threshold}%</span>
                  </Label>
                  <Input
                    type="number"
                    value={alertConfig.noshow_threshold}
                    onChange={(e) => setAlertConfig({ ...alertConfig, noshow_threshold: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    step="0.5"
                  />
                  <p className="text-xs text-[#57534E]">Alert wanneer no-show percentage boven dit komt</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-[#C2410C]" />
                      Inkoop Kosten Drempel (%)
                    </span>
                    <span className="text-sm text-[#C2410C] font-medium">{alertConfig.inkoop_cost_threshold}%</span>
                  </Label>
                  <Input
                    type="number"
                    value={alertConfig.inkoop_cost_threshold}
                    onChange={(e) => setAlertConfig({ ...alertConfig, inkoop_cost_threshold: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    step="0.5"
                    data-testid="inkoop-cost-threshold"
                  />
                  <p className="text-xs text-[#57534E]">Alert wanneer inkoop kosten (facturen + bestellingen) boven dit percentage van omzet komt</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-amber-500" />
                      Kosten Trend Alert (%)
                    </span>
                    <span className="text-sm text-amber-600 font-medium">+{alertConfig.inkoop_trend_threshold}%</span>
                  </Label>
                  <Input
                    type="number"
                    value={alertConfig.inkoop_trend_threshold}
                    onChange={(e) => setAlertConfig({ ...alertConfig, inkoop_trend_threshold: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="200"
                    step="5"
                    data-testid="inkoop-trend-threshold"
                  />
                  <p className="text-xs text-[#57534E]">Alert wanneer inkoop kosten deze week meer dan dit percentage hoger zijn dan vorige week</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notification Preferences */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-500" />
                Notificatie Voorkeuren
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>E-mailadres voor alerts</Label>
                <Input
                  type="email"
                  value={alertConfig.email}
                  onChange={(e) => setAlertConfig({ ...alertConfig, email: e.target.value })}
                  placeholder="uw@email.nl"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-[#57534E]" />
                    <div>
                      <p className="font-medium">E-mail Alerts</p>
                      <p className="text-sm text-[#57534E]">Ontvang directe email bij overschrijding</p>
                    </div>
                  </div>
                  <Switch
                    checked={alertConfig.email_alerts}
                    onCheckedChange={(checked) => setAlertConfig({ ...alertConfig, email_alerts: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-[#57534E]" />
                    <div>
                      <p className="font-medium">In-App Notificaties</p>
                      <p className="text-sm text-[#57534E]">Toon alerts op het dashboard</p>
                    </div>
                  </div>
                  <Switch
                    checked={alertConfig.in_app_alerts}
                    onCheckedChange={(checked) => setAlertConfig({ ...alertConfig, in_app_alerts: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#57534E]" />
                    <div>
                      <p className="font-medium">Dagelijkse Samenvatting</p>
                      <p className="text-sm text-[#57534E]">Ontvang elke dag een overzicht</p>
                    </div>
                  </div>
                  <Switch
                    checked={alertConfig.daily_summary}
                    onCheckedChange={(checked) => setAlertConfig({ ...alertConfig, daily_summary: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#57534E]" />
                    <div>
                      <p className="font-medium">Wekelijkse Samenvatting</p>
                      <p className="text-sm text-[#57534E]">Ontvang wekelijks een overzicht</p>
                    </div>
                  </div>
                  <Switch
                    checked={alertConfig.weekly_summary}
                    onCheckedChange={(checked) => setAlertConfig({ ...alertConfig, weekly_summary: checked })}
                  />
                </div>
              </div>

              {(alertConfig.daily_summary || alertConfig.weekly_summary) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                  {alertConfig.daily_summary && (
                    <div className="space-y-2">
                      <Label>Tijd dagelijkse samenvatting</Label>
                      <Input
                        type="time"
                        value={alertConfig.summary_time}
                        onChange={(e) => setAlertConfig({ ...alertConfig, summary_time: e.target.value })}
                      />
                    </div>
                  )}
                  {alertConfig.weekly_summary && (
                    <div className="space-y-2">
                      <Label>Dag wekelijkse samenvatting</Label>
                      <Select
                        value={alertConfig.summary_day}
                        onValueChange={(value) => setAlertConfig({ ...alertConfig, summary_day: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="maandag">Maandag</SelectItem>
                          <SelectItem value="dinsdag">Dinsdag</SelectItem>
                          <SelectItem value="woensdag">Woensdag</SelectItem>
                          <SelectItem value="donderdag">Donderdag</SelectItem>
                          <SelectItem value="vrijdag">Vrijdag</SelectItem>
                          <SelectItem value="zaterdag">Zaterdag</SelectItem>
                          <SelectItem value="zondag">Zondag</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Revenue Reminder Settings */}
          <Card className="card-base border-2 border-[#C2410C]/20">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#C2410C]" />
                Omzet Herinneringen
              </CardTitle>
              <p className="text-sm text-[#57534E]">
                Ontvang automatisch een e-mail herinnering om je omzet in te vullen
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#C2410C]/5 to-[#C2410C]/10 rounded-xl border border-[#C2410C]/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#C2410C]/10 flex items-center justify-center">
                    <CalendarDays className="w-5 h-5 text-[#C2410C]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1C1917]">Omzet Herinnering Activeren</p>
                    <p className="text-sm text-[#57534E]">
                      {alertConfig.revenue_reminder_enabled 
                        ? `Je ontvangt ${alertConfig.revenue_reminder_frequency} een herinnering` 
                        : 'Schakel in om herinneringen te ontvangen'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={alertConfig.revenue_reminder_enabled}
                  onCheckedChange={(checked) => setAlertConfig({ ...alertConfig, revenue_reminder_enabled: checked })}
                  data-testid="revenue-reminder-toggle"
                />
              </div>

              {alertConfig.revenue_reminder_enabled && (
                <div className="space-y-4 pt-4 border-t border-stone-200">
                  {/* Frequency Selection */}
                  <div className="space-y-2">
                    <Label className="text-[#1C1917] font-medium">Hoe vaak wil je een herinnering?</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { value: 'dagelijks', label: 'Dagelijks', desc: 'Elke dag' },
                        { value: 'wekelijks', label: 'Wekelijks', desc: 'Eens per week' },
                        { value: 'maandelijks', label: 'Maandelijks', desc: 'Eens per maand' },
                        { value: 'jaarlijks', label: 'Jaarlijks', desc: 'Eens per jaar' }
                      ].map(freq => (
                        <div 
                          key={freq.value}
                          onClick={() => setAlertConfig({ ...alertConfig, revenue_reminder_frequency: freq.value })}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            alertConfig.revenue_reminder_frequency === freq.value
                              ? 'border-[#C2410C] bg-[#C2410C]/5'
                              : 'border-stone-200 hover:border-stone-300'
                          }`}
                          data-testid={`reminder-freq-${freq.value}`}
                        >
                          <p className={`font-semibold ${alertConfig.revenue_reminder_frequency === freq.value ? 'text-[#C2410C]' : 'text-[#1C1917]'}`}>
                            {freq.label}
                          </p>
                          <p className="text-xs text-[#57534E] mt-1">{freq.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Time Settings based on frequency */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tijd van de herinnering</Label>
                      <Input
                        type="time"
                        value={alertConfig.revenue_reminder_time}
                        onChange={(e) => setAlertConfig({ ...alertConfig, revenue_reminder_time: e.target.value })}
                        data-testid="reminder-time-input"
                      />
                      <p className="text-xs text-[#57534E]">
                        {alertConfig.revenue_reminder_frequency === 'dagelijks' 
                          ? 'Elke dag om dit tijdstip' 
                          : alertConfig.revenue_reminder_frequency === 'wekelijks'
                            ? 'Op de gekozen dag om dit tijdstip'
                            : alertConfig.revenue_reminder_frequency === 'maandelijks'
                              ? 'Op de eerste dag van de maand'
                              : 'Op 1 januari om dit tijdstip'}
                      </p>
                    </div>

                    {alertConfig.revenue_reminder_frequency === 'wekelijks' && (
                      <div className="space-y-2">
                        <Label>Dag van de week</Label>
                        <Select
                          value={alertConfig.revenue_reminder_day}
                          onValueChange={(value) => setAlertConfig({ ...alertConfig, revenue_reminder_day: value })}
                        >
                          <SelectTrigger data-testid="reminder-day-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="maandag">Maandag</SelectItem>
                            <SelectItem value="dinsdag">Dinsdag</SelectItem>
                            <SelectItem value="woensdag">Woensdag</SelectItem>
                            <SelectItem value="donderdag">Donderdag</SelectItem>
                            <SelectItem value="vrijdag">Vrijdag</SelectItem>
                            <SelectItem value="zaterdag">Zaterdag</SelectItem>
                            <SelectItem value="zondag">Zondag</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-[#57534E]">Ontvang elke {alertConfig.revenue_reminder_day} je herinnering</p>
                      </div>
                    )}
                  </div>

                  {/* Smart Reminder Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-50 to-emerald-100/50 rounded-xl border border-emerald-200">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${alertConfig.smart_reminder ? 'bg-emerald-500' : 'bg-emerald-100'}`}>
                        <TrendingUp className={`w-5 h-5 ${alertConfig.smart_reminder ? 'text-white' : 'text-emerald-500'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-[#1C1917]">🧠 Slimme Herinnering</p>
                        <p className="text-sm text-[#57534E]">
                          {alertConfig.smart_reminder 
                            ? 'Alleen herinneren als omzet nog niet is ingevuld' 
                            : 'Altijd herinneren, ook als omzet al is ingevuld'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={alertConfig.smart_reminder}
                      onCheckedChange={(checked) => setAlertConfig({ ...alertConfig, smart_reminder: checked })}
                      data-testid="smart-reminder-toggle"
                    />
                  </div>

                  {alertConfig.smart_reminder && (
                    <div className="p-4 bg-emerald-50/50 rounded-xl border border-emerald-100">
                      <p className="text-sm text-emerald-800">
                        ✨ <strong>Hoe werkt het?</strong> Het systeem checkt automatisch of je de omzet voor de huidige periode 
                        ({alertConfig.revenue_reminder_frequency === 'dagelijks' ? 'vandaag' : 
                          alertConfig.revenue_reminder_frequency === 'wekelijks' ? 'deze week' : 
                          alertConfig.revenue_reminder_frequency === 'maandelijks' ? 'deze maand' : 'dit jaar'}) 
                        al hebt ingevuld. Alleen als er nog geen omzet is geregistreerd, ontvang je een herinnering.
                      </p>
                    </div>
                  )}

                  {/* Preview */}
                  <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
                    <p className="text-sm font-medium text-[#1C1917] mb-2">📧 Voorbeeld van je herinnering:</p>
                    <div className="text-sm text-[#57534E] space-y-1">
                      <p><strong>Onderwerp:</strong> Vergeet niet je {
                        alertConfig.revenue_reminder_frequency === 'dagelijks' ? 'dag' :
                        alertConfig.revenue_reminder_frequency === 'wekelijks' ? 'week' :
                        alertConfig.revenue_reminder_frequency === 'maandelijks' ? 'maand' : 'jaar'
                      }omzet in te vullen!</p>
                      <p><strong>Frequentie:</strong> {
                        alertConfig.revenue_reminder_frequency === 'dagelijks' ? `Dagelijks om ${alertConfig.revenue_reminder_time}` :
                        alertConfig.revenue_reminder_frequency === 'wekelijks' ? `Elke ${alertConfig.revenue_reminder_day} om ${alertConfig.revenue_reminder_time}` :
                        alertConfig.revenue_reminder_frequency === 'maandelijks' ? `Eerste dag van de maand om ${alertConfig.revenue_reminder_time}` :
                        `1 januari om ${alertConfig.revenue_reminder_time}`
                      }</p>
                      <p><strong>Verzenden naar:</strong> {alertConfig.email || 'Vul hierboven je e-mailadres in'}</p>
                      {alertConfig.smart_reminder && (
                        <p className="text-emerald-600"><strong>Slimme check:</strong> Alleen als omzet nog niet ingevuld</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Push Notifications Settings */}
          <Card className="card-base border-2 border-blue-500/20">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-blue-500" />
                Push Notificaties
              </CardTitle>
              <p className="text-sm text-[#57534E]">
                Ontvang direct een melding op je apparaat - ook als de browser gesloten is
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {!pushSupported ? (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-sm text-amber-800">
                    ⚠️ Push notificaties worden niet ondersteund in deze browser. 
                    Probeer Chrome, Firefox of Edge voor de beste ervaring.
                  </p>
                </div>
              ) : pushPermission === 'denied' ? (
                <div className="p-4 bg-rose-50 rounded-xl border border-rose-200">
                  <p className="text-sm text-rose-800">
                    ❌ Je hebt notificaties geblokkeerd voor deze website. 
                    Ga naar je browser instellingen om dit te wijzigen.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-xl border border-blue-200">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${alertConfig.push_enabled ? 'bg-blue-500' : 'bg-blue-100'}`}>
                        <Bell className={`w-5 h-5 ${alertConfig.push_enabled ? 'text-white' : 'text-blue-500'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-[#1C1917]">Push Notificaties</p>
                        <p className="text-sm text-[#57534E]">
                          {alertConfig.push_enabled 
                            ? '✅ Actief - je ontvangt meldingen op dit apparaat' 
                            : 'Schakel in om meldingen te ontvangen'}
                        </p>
                      </div>
                    </div>
                    {alertConfig.push_enabled ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleDisablePush}
                        className="text-rose-600 border-rose-200 hover:bg-rose-50"
                      >
                        Uitschakelen
                      </Button>
                    ) : (
                      <Button 
                        onClick={handleEnablePush}
                        disabled={enablingPush}
                        className="bg-blue-500 hover:bg-blue-600 text-white"
                        data-testid="enable-push-btn"
                      >
                        {enablingPush ? 'Inschakelen...' : 'Inschakelen'}
                      </Button>
                    )}
                  </div>

                  {alertConfig.push_enabled && (
                    <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl border border-stone-200">
                      <div>
                        <p className="text-sm font-medium text-[#1C1917]">Test je push notificatie</p>
                        <p className="text-xs text-[#57534E]">Verzend een test melding naar dit apparaat</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleTestPushNotification}
                        data-testid="test-push-btn"
                      >
                        <Bell className="w-4 h-4 mr-2" />
                        Test Notificatie
                      </Button>
                    </div>
                  )}

                  <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
                    <p className="text-sm font-medium text-[#1C1917] mb-2">📱 Hoe werkt het?</p>
                    <ul className="text-sm text-[#57534E] space-y-1 list-disc list-inside">
                      <li>Push notificaties werken ook als je browser gesloten is</li>
                      <li>Je ontvangt een melding op je desktop of mobiel</li>
                      <li>Klik op de notificatie om direct naar de omzet pagina te gaan</li>
                      <li>Werkt met dezelfde frequentie als je e-mail herinneringen</li>
                    </ul>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={handleSaveAlertConfig}
              disabled={savingAlerts}
              className="btn-primary flex-1"
              data-testid="save-alerts-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {savingAlerts ? 'Opslaan...' : 'Instellingen Opslaan'}
            </Button>
            <Button
              onClick={handleTestAlerts}
              disabled={testingAlerts || !alertConfig.is_active}
              variant="outline"
              className="flex-1"
              data-testid="test-alerts-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              {testingAlerts ? 'Testen...' : 'Test Alerts Nu'}
            </Button>
          </div>
        </TabsContent>

        {/* Subscription Tab (Company users only) */}
        {user?.role !== 'admin' && (
          <TabsContent value="subscription" className="mt-6">
            <SubscriptionSettingsComponent 
              api={api} 
              subscription={subscription}
              refreshSubscription={refreshSubscription}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default InstellingenPage;
